import {
    API,
    graphqlOperation
} from "aws-amplify";
import {
    CreateDimensionMutation,
    DimensionType,
    GetDimensionQuery,
    GetDimensionQueryVariables,
    ListDimensionsByParentQuery,
    ListDimensionsByParentQueryVariables,
    ListDimensionsByPropertyQuery,
    ListDimensionsByPropertyQueryVariables,
    MeasurementUnit,
    UpdateDimensionMutation,
    UpdateDimensionMutationVariables
} from "../../../../API";
import {
    createDimension,
    updateDimension
} from "../../../../graphql/mutations";
import {
    getDimension,
    listDimensionsByParent,
    listDimensionsByProperty
} from "../../../../graphql/queries";

import ClientLogger from "../../../logging/ClientLogger";
import { CreateDimensionError } from '../error/CreateDimensionError';
import { Dimension } from "../../../../API";
import DimensionDAO from "../DimensionDAO";
import DimensionRecordAlreadyExistsError from "../error/DimensionRecordAlreadyExistsError";
import Envelope from "../../../envelope/Envelope";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { IDTokenSupplier } from "../../../auth/IDTokenSupplier";
import { ListResponse } from "../../../util/data/list/ListResponse";
import { ProducerModelPredicate } from "@aws-amplify/datastore";

export class GraphQLDimensionDAO implements DimensionDAO {

    private static readonly CREATE_ATTEMPT_METRIC_NAME = "GraphQLDimensionDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "GraphQLDimensionDAO.Create.Failure";

    private readonly api: typeof API;
    private readonly gqlOperation: typeof graphqlOperation;
    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        api: typeof API,
        gqlOperation: typeof graphqlOperation,
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.api = api;
        this.gqlOperation = gqlOperation;
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async getDimensionById(dimensionId: string): Promise<Dimension> {
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: GetDimensionQueryVariables = {
                id: dimensionId
            }
            const result: GraphQLResult<GetDimensionQuery> = await this.api.graphql(
                this.gqlOperation(
                    getDimension,
                    variables,
                    authToken
                )
            ) as GraphQLResult<GetDimensionQuery>;
            return result.data?.getDimension as Dimension;
        } catch (error) {
            console.log('Failed to get dimension: ', dimensionId, error);
            throw new Error("Failed to get Dimension")
        }
    }

    public async create(
        record: Dimension
    ): Promise<Dimension> {
        this.logger.info(
            `Creating Dimension record with id=${record.id}`,
            record,
            [GraphQLDimensionDAO.CREATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<CreateDimensionMutation> = await this.api.graphql(
                this.gqlOperation(
                    createDimension,
                    { input: record },
                    authToken
                )
            ) as GraphQLResult<CreateDimensionMutation>;
            return result.data?.createDimension as Dimension;
        } catch (error) {
            if ((error as any)?.errors && (error as any)?.errors[0].errorType === "ConditionalCheckFailedException") {
                const errorMessage = `Dimension record already exists: ${record.id}`;
                this.logger.warn(
                    errorMessage,
                    error,
                    [GraphQLDimensionDAO.CREATE_FAILURE_METRIC_NAME]
                );
                throw new DimensionRecordAlreadyExistsError(
                    errorMessage
                );
            }
            const errorMessage = `Unable to create Dimension record with id=${record.id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLDimensionDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new CreateDimensionError(errorMessage, {
                cause: error as Error
            });
        }
    }

    public async createDimensionFromV2Envelope(
        envelope: Envelope,
        propertyId: string,
        parentId: string
    ): Promise<Envelope> {
        throw new Error("Method not implemented.");
    }

    public async getDimensionsByParentId(
        parentId: string
    ): Promise<Dimension[]> {
        throw new Error("Method not implemented.");
    }

    public async updateDimension(
        dimensionId: string,
        dimension: Dimension
    ): Promise<Dimension> {
        const existingDimension: Dimension = await this.getDimensionById(dimension.id);
        try {
            const variables: UpdateDimensionMutationVariables = {
                input: {
                    id: dimension.id,
                    parentId: dimension.parentId,
                    propertyId: dimension.propertyId,
                    measurement: {
                        measurementUnit: (dimension.measurement?.measurementUnit as MeasurementUnit),
                        width: dimension.measurement?.width ?? 0,
                        length: dimension.measurement?.length ?? 0,
                        dimensionType: (dimension.measurement?.dimensionType ?? 'SQUARE') as DimensionType
                    },
                    _version: existingDimension._version
                }
            };
            const authToken: string = await this.idTokenSupplier.get();
            const result: GraphQLResult<UpdateDimensionMutation> = await this.api.graphql(
                this.gqlOperation(
                    updateDimension,
                    variables,
                    authToken
                )
            ) as GraphQLResult<UpdateDimensionMutation>;
            return result.data?.updateDimension as Dimension;
        } catch (error) {
            console.log('Failed to update dimension: ', dimension, error);
            throw new Error("Failed to update Dimension")
        }
    }

    public async updateDimensionByEnvelope(
        dimensionId: string,
        dimension: Envelope
    ): Promise<Envelope> {
        throw new Error("Method not implemented.");
    }

    public async deleteDimension(
        dimensionId: string
    ): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public async getDimensionEnvelopeById(
        dimensionId: string
    ): Promise<Envelope | undefined> {
        throw new Error("Method not implemented.");
    }

    public getDimensionsByParentIdWithLimit(
        // This type needs to be abstracted - see the TODO comment on the interface.
        predicate: ProducerModelPredicate<Dimension>,
        limit: number
    ): Promise<Dimension[]> {
        throw new Error("Method not implemented.");
    }

    public async listAllByParentId(
        parentId: string,
    ): Promise<Dimension[]> {
        try {
            const variables: ListDimensionsByParentQueryVariables = {
                parentId: parentId,
            };
            let allDimensions: Dimension[] = [];

            const authToken: string = await this.idTokenSupplier.get();
            do {
                const response: GraphQLResult<ListDimensionsByParentQuery> = await this.api.graphql(
                    this.gqlOperation(
                        listDimensionsByParent,
                        variables,
                        authToken
                    )) as GraphQLResult<ListDimensionsByParentQuery>;
                const listResult = response.data?.listDimensionsByParent?.items as Dimension[];
                allDimensions.push(...listResult);
                variables.nextToken = response.data?.listDimensionsByParent?.nextToken;
            } while (!!variables.nextToken);

            return allDimensions;
        } catch (error) {
            console.log('error: ', error);
            throw new Error(
                "Failed to list Dimensions by parentId" + error
            );
        }
    }

    public async getDimensionsByPropertyId(
        propertyId: string,
        nextToken?: string
    ): Promise<ListResponse<Dimension>> {
        try {
            const variables: ListDimensionsByPropertyQueryVariables = {
                propertyId: propertyId,
                nextToken: nextToken
            };

            const authToken: string = await this.idTokenSupplier.get();
            const response: GraphQLResult<ListDimensionsByPropertyQuery> = await this.api.graphql(
                this.gqlOperation(
                    listDimensionsByProperty,
                    variables,
                    authToken
                )) as GraphQLResult<ListDimensionsByPropertyQuery>;
            return {
                items: response.data?.listDimensionsByProperty?.items as Dimension[],
                nextToken: response.data?.listDimensionsByProperty?.nextToken ?? null
            };
        } catch (error) {
            console.log('error: ', error);
            throw new Error(
                "Failed to list Dimensions by propertyId" + error
            );
        }
    }
}
